/**
 * Demo 统一入口页面（公共组件）
 *
 * 接收 demoRoutes 和 isDemoMode 作为 props，不依赖具体应用的文件
 * @since 6666-AI组件复用基建 T1.4: aiTags 多标签筛选 + complexity badge + 移除 category 筛选条
 */

import React, { useState, useMemo } from 'react';
import { Input, Card, Tag, Empty } from '@bedrock/components';
import { useHistory } from 'react-router-dom';
import type { Complexity } from '../types';

/** aiTags 分组展示（按 6 大维度组织，与 ai-tags-dictionary.md 同步） */
const AI_TAG_GROUPS: { label: string; tags: string[] }[] = [
  { label: '数据', tags: ['form', 'table', 'list', 'tree', 'card', 'chart', 'timeline'] },
  { label: '交互', tags: ['select', 'search', 'filter', 'sort', 'paginate', 'upload', 'import', 'export', 'drag', 'batch', 'copy'] },
  { label: 'UI', tags: ['modal', 'drawer', 'panel', 'tabs', 'popover', 'tooltip', 'breadcrumb', 'stepper'] },
  { label: '业务', tags: ['supplier', 'bidding', 'contract', 'demand', 'invoice', 'finance', 'settlement', 'auth', 'serviceConfig'] },
  { label: '能力', tags: ['multi', 'single', 'editable', 'readonly', 'nested', 'lazy', 'collapsible', 'fullscreen', 'responsive'] },
  { label: '辅助', tags: ['demo', 'showcase', 'test', 'hot-reload'] },
];

/** 所有标签的扁平集合 */
const ALL_AI_TAGS = AI_TAG_GROUPS.flatMap((g) => g.tags);

/** complexity 色值映射 */
const COMPLEXITY_COLORS: Record<Complexity, string> = {
  simple: '#52c41a',
  medium: '#faad14',
  complex: '#ff4d4f',
};

export interface DemoRouteItem {
  path: string;
  title: string;
  description?: string;
  /** @deprecated 使用 aiTags 替代 */
  category?: string[] | string;
  author?: string;
  date?: string;
  filePath?: string;
  // ── T1.4 新增字段 ──
  aiTags?: string[];
  complexity?: Complexity;
  sourceComponent?: string;
}

/** 将 category 统一转为数组（兼容旧字符串和新数组格式） */
function normalizeCategory(category?: string[] | string): string[] {
  if (!category) return [];
  if (Array.isArray(category)) return category;
  return category
    .split(/[,，]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export interface DemoEntryProps {
  /** Demo 路由列表 */
  demoRoutes: DemoRouteItem[];
  /** 是否启用 Demo 模式 */
  isDemoMode: boolean;
  /** 自定义标题 */
  title?: string;
  /** 自定义空状态描述 */
  emptyDescription?: string;
}

export default function DemoEntry({
  demoRoutes,
  isDemoMode,
  title = 'Demo 展示中心',
  emptyDescription = 'Demo 模式未启用',
}: DemoEntryProps) {
  const history = useHistory();
  const [searchKey, setSearchKey] = useState('');
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());

  /** 切换 tag 选中状态（AND 语义） */
  const toggleTag = (tag: string) => {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  };

  /** 获取 demo 的有效 aiTags（优先用 aiTags，回退到 category） */
  const getEffectiveTags = (demo: DemoRouteItem): string[] => {
    if (demo.aiTags && demo.aiTags.length > 0) return demo.aiTags;
    // 回退：将 category 映射到 aiTags（过渡兼容）
    const cats = normalizeCategory(demo.category);
    if (cats.length === 0) return [];
    return cats
      .flatMap((c) => {
        switch (c) {
          case '核心': return ['demo'];
          case '业务': return ['contract'];
          case '表单': return ['form'];
          case '表格': return ['table'];
          default: return [];
        }
      })
      .filter((t) => ALL_AI_TAGS.includes(t));
  };

  /** 每个 tag 在全量 demo 中的命中数（统计所有 tag，不限于预定义分组） */
  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const demo of demoRoutes) {
      const tags = new Set(getEffectiveTags(demo));
      for (const tag of tags) {
        counts[tag] = (counts[tag] || 0) + 1;
      }
    }
    return counts;
  }, [demoRoutes]);

  /** 动态分组：预定义分组 + 未分类（收集不在预定义分组中的 tag） */
  const displayGroups = useMemo(() => {
    const unclassifiedTags: string[] = [];
    for (const tag of Object.keys(tagCounts)) {
      if (!ALL_AI_TAGS.includes(tag)) {
        unclassifiedTags.push(tag);
      }
    }
    unclassifiedTags.sort();
    if (unclassifiedTags.length > 0) {
      return [...AI_TAG_GROUPS, { label: '未分类', tags: unclassifiedTags }];
    }
    return AI_TAG_GROUPS;
  }, [tagCounts]);

  const filteredDemos = useMemo(() => {
    let result = demoRoutes;

    // aiTags 多标签筛选（AND 语义：选中 tag 必须全部匹配）
    if (activeTags.size > 0) {
      result = result.filter((demo) => {
        const demoActiveTags = new Set(getEffectiveTags(demo));
        return Array.from(activeTags).every((tag) => demoActiveTags.has(tag));
      });
    }

    // 搜索关键字过滤（标题 / 描述 / aiTags）
    if (searchKey) {
      const lowerKey = searchKey.toLowerCase();
      result = result.filter(
        (demo) =>
          demo.path.toLowerCase().includes(lowerKey) ||
          demo.title?.toLowerCase().includes(lowerKey) ||
          demo.description?.toLowerCase().includes(lowerKey) ||
          getEffectiveTags(demo).some((t) => t.includes(lowerKey))
      );
    }

    return result;
  }, [searchKey, activeTags, demoRoutes]);

  if (!isDemoMode) {
    return <Empty description={emptyDescription} />;
  }

  return (
    <div className="bg-bg-1 p-2 min-h-screen bg-white relative">
      <h1 className="text-2xl font-bold text-color-1 mb-6">{title}</h1>

      {/* aiTags 多标签筛选条（AND 语义） */}
      <div className="mb-4 ml-2">
        {displayGroups.map((group) => (
          <div key={group.label} className="flex items-center gap-1 mb-1">
            <span className="text-xs text-color-3 w-8 shrink-0">{group.label}</span>
            {group.tags.map((tag) => {
              const count = tagCounts[tag] || 0;
              if (count === 0) return null;
              const isActive = activeTags.has(tag);
              const label = `${tag} (${count})`;
              return (
                <Tag
                  key={tag}
                  size="xSmall"
                  color={isActive ? 'blue' : 'light-grey'}
                  className="cursor-pointer"
                  text={label}
                  onClick={() => toggleTag(tag)}
                />
              );
            })}
          </div>
        ))}
        {activeTags.size > 0 && (
          <div className="mt-1">
            <span
              className="px-2 py-0.5 rounded cursor-pointer text-xs text-color-3 hover:text-color-1"
              onClick={() => setActiveTags(new Set())}
            >
              清除筛选
            </span>
          </div>
        )}
      </div>

      {/* 搜索框 */}
      <div className="mb-6 ml-2" style={{ width: 480 }}>
        <Input.Search
          placeholder="搜索 Demo（标题 / 描述 / aiTags）"
          value={searchKey}
          onChange={(e) => setSearchKey(e.target.value)}
          allowClear
          size="large"
        />
      </div>

      {/* Demo 卡片网格 */}
      {filteredDemos.length === 0 ? (
        <Empty description="暂无 Demo" />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {filteredDemos.map((demo) => {
            const tags = getEffectiveTags(demo);
            const displayTags = tags.slice(0, 3); // 只显示前 3 个
            return (
              <div
                key={demo.path}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => history.push(demo.path)}
              >
                <Card className="text-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-medium text-color-1 truncate flex-1">
                      {demo.title}
                    </h3>
                    {demo.complexity && (
                      <span
                        className="text-xs px-1.5 py-0.5 rounded"
                        style={{
                          color: COMPLEXITY_COLORS[demo.complexity],
                          backgroundColor: `${COMPLEXITY_COLORS[demo.complexity]}15`,
                        }}
                      >
                        {demo.complexity}
                      </span>
                    )}
                  </div>
                  {demo.description && (
                    <p className="text-color-2 text-xs mb-2 line-clamp-1">
                      {demo.description}
                    </p>
                  )}
                  {displayTags.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap mb-1">
                      {displayTags.map((tag) => (
                        <Tag key={tag} size="small" text={tag} />
                      ))}
                      {tags.length > 3 && (
                        <span className="text-xs text-color-4">+{tags.length - 3}</span>
                      )}
                    </div>
                  )}
                  <div className="mt-1 text-xs text-color-4 truncate">{demo.path}</div>
                  {demo.filePath && (
                    <div className="mt-1 text-xs text-color-3 truncate" title={demo.filePath}>
                      📁 {demo.filePath}
                    </div>
                  )}
                </Card>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
